/**
 * Referral attribution has to survive the short trip from a shared link to
 * sign-up. It is deliberately client-side and short-lived: the server remains
 * the authority and binds the inviter exactly once when the account is made.
 */

const STORAGE_KEY = 'referral:pending'
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000
const PARAMS = ['ref', 'referral', 'referralCode', 'usercode'] as const

interface StoredReferral {
  code: string
  capturedAt: number
}

export function normaliseReferralCode(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null
  const code = raw.trim().toUpperCase()
  if (!/^[A-Z0-9_-]{4,32}$/.test(code)) return null
  return code
}

function codeFrom(params: URLSearchParams): string | null {
  for (const key of PARAMS) {
    const code = normaliseReferralCode(params.get(key))
    if (code !== null) return code
  }
  return null
}

/** Captures both ordinary `?ref=` links and hash-router links from references. */
export function captureReferralCode(location: Location = window.location): string | null {
  const searchCode = codeFrom(new URLSearchParams(location.search))
  const hashQuery = location.hash.includes('?') ? location.hash.slice(location.hash.indexOf('?')) : ''
  const code = searchCode ?? codeFrom(new URLSearchParams(hashQuery))
  if (code === null) return readPendingReferralCode()

  try {
    const value: StoredReferral = { code, capturedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // The field on /signup will still read the URL when storage is unavailable.
  }
  return code
}

export function readPendingReferralCode(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return ''
    const parsed = JSON.parse(raw) as Partial<StoredReferral>
    const code = normaliseReferralCode(parsed.code)
    if (
      code === null ||
      typeof parsed.capturedAt !== 'number' ||
      Date.now() - parsed.capturedAt > MAX_AGE_MS
    ) {
      localStorage.removeItem(STORAGE_KEY)
      return ''
    }
    return code
  } catch {
    return ''
  }
}

/**
 * The server rejected this code (unknown or its owner is no longer active).
 * Forget it so a later plain visit to sign-up does not keep prefilling a code
 * that can only fail — but leave a different, newer link's code alone.
 */
export function forgetRejectedReferralCode(code: string): void {
  const rejected = normaliseReferralCode(code)
  if (rejected === null || readPendingReferralCode() !== rejected) return
  clearPendingReferralCode()
}

export function clearPendingReferralCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // The account is already bound server-side; stale local state is harmless.
  }
}
